import React, { useState } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { DownloadAdminBackupAPI } from '../../../../api/AdminAPI';
import { formatDate } from '../../../../utils/Utils';

/**
 * Formata bytes em uma unidade legível (B/KB/MB), com 1 casa decimal.
 * @param {number} bytes
 * @returns {string}
 */
export function formatBackupSize(bytes) {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

/**
 * Formata uma data 'Y-m-d H:i:s' (created_at/modified_at) usando formatDate() do projeto.
 * @param {string|null} dateStr
 * @returns {string}
 */
export function formatBackupDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return formatDate(date);
}

/**
 * BackupRow - Linha da tabela de backups do banco de dados (Admin Geral > Backups)
 *
 * Controla seu próprio estado de loading durante o download e delega falhas
 * ao callback `onDownloadError` (a página exibe o Toast).
 *
 * @param {Object} props
 * @param {Object} props.backup - { name, size, created_at, modified_at }
 * @param {Function} props.onDownloadError - callback(message) chamado quando o download falha
 */
function BackupRow({ backup, onDownloadError }) {
  const { name, size, created_at } = backup;
  const [downloading, setDownloading] = useState(false);

  const handleDownload = () => {
    if (downloading) return;
    setDownloading(true);
    DownloadAdminBackupAPI(name)
      .catch((err) => {
        onDownloadError?.(err?.message || 'Erro ao baixar o backup. Tente novamente.');
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{name}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{formatBackupDate(created_at)}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">{formatBackupSize(size)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Button
          size="small"
          variant="outlined"
          startIcon={downloading ? <CircularProgress size={14} /> : <DownloadOutlinedIcon fontSize="small" />}
          onClick={handleDownload}
          disabled={downloading}
        >
          Baixar
        </Button>
      </TableCell>
    </TableRow>
  );
}

export default BackupRow;
